import {PieceInspectorState, initialPieceInspectorState} from './piece-inspector.state';
import {hashObject} from '../../../shared/utils/hash-object';
import {PieceInspectorAction, PieceInspectorActionType} from './piece-inspector.actions';
import {PieceInspectorScanPiece} from '../../models/piece-inspector-scan-piece';

export function pieceInspectorReducer(
  state = initialPieceInspectorState,
  action: PieceInspectorAction
): PieceInspectorState {
  switch (action.type) {
    case PieceInspectorActionType.GET_BATCHES_SUCCESS: {
      const queryBatchesMap = {
        ...state.queryBatchesMap,
        [hashObject(action.payload.params)]: action.payload.batches,
      };
      return {...state, queryBatchesMap, batchesCount: action.payload.batchesCount};
    }
    case PieceInspectorActionType.GET_RECENT_BATCHES_SUCCESS:
      return {...state, recentBatches: action.payload.batches};
    case PieceInspectorActionType.GET_BATCH_SUCCESS:
      return {...state, singleBatch: action.payload.batch};
    case PieceInspectorActionType.CLEAR_BATCH:
      return {...state, singleBatch: null};
    case PieceInspectorActionType.GET_BATCH_FROM_SCAN_SUCCESS:
      return {...state, batchesFromScan: action.payload.batches};
    case PieceInspectorActionType.CLEAR_BATCH_FROM_SCAN:
      return {...state, batchesFromScan: null};
    case PieceInspectorActionType.SCAN_PIECE_SUCCESS:
      return {...state, scanPiece: mergeScanLog(action.payload.piece, state.scanPiece)};
    case PieceInspectorActionType.CLEAR_SCAN_PIECE:
      return {...state, scanPiece: null};
    case PieceInspectorActionType.NEW_BATCH_SUCCESS:
      return {...state, createdBatch: action.payload.batch};
    case PieceInspectorActionType.GET_CLOSEOUT_BATCHES_SUCCESS: {
      const queryCloseoutBatchesMap = {
        ...state.queryCloseoutBatchesMap,
        [hashObject(action.payload.params)]: action.payload.batches,
      };
      return {...state, queryCloseoutBatchesMap, batchesCount: action.payload.batchesCount};
    }
    case PieceInspectorActionType.UPDATE_GAME_SUCCESS:
      return {...state, gameState: action.payload.status};
    case PieceInspectorActionType.CLEAR_GAME_STATE:
      return {...state, gameState: ''};
    case PieceInspectorActionType.CLEAR_GAME_DETAILS:
      return {...state, singleBatch: null};
    default:
      return state;
  }
}

/**
 * Scan requests send the last known LogID. With LogID 0 the API returns the full
 * ScanLog; with a real LogID it returns an empty ScanLog and only the scan result
 * itself, which is also a log row. Merge whatever came back, plus the result as a
 * row, into the log already in the store so the page and the right drawer keep
 * the full history. If rows carry no LogID the incoming piece is taken as-is,
 * which preserves the old full-replace behaviour for endpoints without LogIDs.
 */
export function mergeScanLog(
  piece: PieceInspectorScanPiece,
  previous: PieceInspectorScanPiece
): PieceInspectorScanPiece {
  if (!piece) {
    return piece;
  }
  const existing = previous?.scanLog || [];
  // The result row goes first so a matching row from the API's ScanLog wins the dedupe.
  const incoming = [...(piece.logId ? [toScanLogRow(piece)] : []), ...(piece.scanLog || [])];
  const rows = [...existing, ...incoming];
  if (!rows.length || rows.some(entry => !entry.logId)) {
    return piece;
  }
  const byLogId = new Map<number, PieceInspectorScanPiece>();
  rows.forEach(entry => byLogId.set(entry.logId, entry));
  const scanLog = Array.from(byLogId.values()).sort((a, b) => a.logId - b.logId);
  return {...piece, scanLog};
}

/** The scan result carries the same fields as a ScanLog row, minus the nested log and batch totals. */
function toScanLogRow(piece: PieceInspectorScanPiece): PieceInspectorScanPiece {
  return {...piece, scanLog: null, statistics: null, pieceCount: 0, pieceCountMessage: null};
}
